import { useCallback } from 'react';
import { UserProfile } from 'src/types/common.types';
import { JWTContextType } from 'src/types/auth.types';
import { useAuthState, Types } from 'src/contexts';
import { useAuthManagement } from './useAuthManagement';

// ----------------------------------------------------------------------

/**
 * Enhanced useAuth hook that combines state management with business logic
 * Follows proper separation of concerns - no hooks in contexts
 */
export const useAuth = (): JWTContextType => {
  const authState = useAuthState();
  const { login: loginWithStorage, logout: logoutWithStorage } = useAuthManagement();

  // Login wrapper that updates state
  const login = useCallback(async (token: string, profile: UserProfile): Promise<void> => {
    try {
      await loginWithStorage(token, profile);
      authState.dispatch({
        type: Types.Login,
        payload: { profile },
      });
    } catch (error) {
      console.error('useAuth: Login failed:', error);
      throw error;
    }
  }, [authState, loginWithStorage]);

  // Logout wrapper that updates state
  const logout = useCallback(async (): Promise<void> => {
    try {
      // First update state
      authState.dispatch({ type: Types.Logout });
      
      // Then handle cleanup
      await logoutWithStorage();
    } catch (error) {
      console.error('useAuth: Logout failed:', error);
      // Even if logout fails, we should clear the state
      authState.dispatch({ type: Types.Logout });
    }
  }, [authState, logoutWithStorage]);

  return {
    // State from context
    isAuthenticated: authState.isAuthenticated,
    isInitialized: authState.isInitialized,
    isOnboarded: authState.isOnboarded,
    profile: authState.profile,
    
    // Business logic from hook
    method: 'jwt' as const,
    login,
    logout,
  };
};
